import { useMemo } from 'react';
import { Page, PageHeader, SectionTitle } from '@/client/components/AppShell';
import { Badge, Card, EmptyState, SkeletonList } from '@/client/components/ui';
import { useWorkspace } from '@/client/store';
import { formatShort } from '@/domain/dates';
import { computeVolunteerStats } from '@/domain/stats';

/**
 * The sanity check on the point system (spec 8).
 *
 * Charts are plain SVG: a charting library would cost more bytes than the
 * whole rest of the app, and three bar charts do not need one.
 */
export function Reports() {
  const { data: workspace, isPending } = useWorkspace();

  const stats = useMemo(() => {
    if (!workspace) return [];
    return computeVolunteerStats(
      workspace.volunteers,
      workspace.assignments,
      workspace.settings,
      workspace.today,
      { reportingWindowWeeks: 13 },
    ).filter((row) => row.status !== 'Removed');
  }, [workspace]);

  const hasHistory = stats.some((row) => row.completed + row.bailed + row.riderCanceled > 0);

  return (
    <>
      <PageHeader title="Reports" subtitle="Last 3 months" />

      <Page>
        {isPending || !workspace ? (
          <SkeletonList rows={4} />
        ) : stats.length === 0 ? (
          <Card>
            <EmptyState
              title="Nothing to report yet"
              description="Add volunteers to get started."
            />
          </Card>
        ) : (
          <>
            <section className="flex flex-col gap-2">
              <SectionTitle
                title="Rides completed"
                hint="The utilisation view: is the load actually spread?"
              />
              <Card className="px-4 py-4">
                {hasHistory ? (
                  <BarChart
                    data={[...stats]
                      .sort((a, b) => b.completed - a.completed)
                      .map((row) => ({ label: row.name, value: row.completed }))}
                  />
                ) : (
                  <p className="py-4 text-center text-sm text-ink-faint">
                    No completed rides recorded yet.
                  </p>
                )}
              </Card>
            </section>

            <section className="flex flex-col gap-2">
              <SectionTitle
                title="Credit standings"
                hint={`Effective credit over the last ${workspace.settings.rollingWindowWeeks} weeks — exactly what the ranker sorts on.`}
              />
              <Card className="divide-y divide-border">
                {[...stats]
                  .sort((a, b) => a.credit - b.credit)
                  .map((row) => (
                    <div
                      key={row.volunteerId}
                      className="flex items-center justify-between gap-3 px-4 py-2.5"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {row.name}
                        {!row.eligibleToday ? (
                          <span className="ml-2 text-xs text-ink-faint">paused</span>
                        ) : null}
                      </span>
                      <span className="shrink-0 text-sm font-semibold tabular-nums">
                        {row.credit}
                      </span>
                    </div>
                  ))}
              </Card>
            </section>

            <section className="flex flex-col gap-2">
              <SectionTitle
                title="Bails and cancellations"
                hint="Tracked but never scored. This is for a conversation, not the math."
              />
              <Card className="divide-y divide-border">
                {stats.filter((row) => row.bailed > 0 || row.riderCanceled > 0).length === 0 ? (
                  <p className="px-4 py-6 text-center text-sm text-ink-faint">
                    No bails or cancellations in the last 3 months.
                  </p>
                ) : (
                  stats
                    .filter((row) => row.bailed > 0 || row.riderCanceled > 0)
                    .sort((a, b) => b.bailed - a.bailed)
                    .map((row) => (
                      <div key={row.volunteerId} className="flex flex-col gap-1 px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <span className="truncate text-sm">{row.name}</span>
                          <span className="flex shrink-0 gap-1.5">
                            {row.bailed > 0 ? (
                              <Badge tone="critical">{row.bailed} bailed</Badge>
                            ) : null}
                            {row.riderCanceled > 0 ? (
                              <Badge tone="caution">{row.riderCanceled} rider canceled</Badge>
                            ) : null}
                          </span>
                        </div>
                        {row.bailDates.length > 0 ? (
                          <p className="text-xs text-ink-faint">
                            {row.bailDates.map(formatShort).join(' · ')}
                          </p>
                        ) : null}
                      </div>
                    ))
                )}
              </Card>
            </section>
          </>
        )}
      </Page>
    </>
  );
}

/**
 * A horizontal bar chart. Uses real layout rather than SVG so the labels wrap
 * and scale with the user's font size.
 */
function BarChart({ data }: { data: { label: string; value: number }[] }) {
  const max = Math.max(1, ...data.map((row) => row.value));

  return (
    <ul className="flex flex-col gap-2.5">
      {data.map((row) => (
        <li key={row.label} className="flex items-center gap-3">
          <span className="w-24 shrink-0 truncate text-xs text-ink-muted" title={row.label}>
            {row.label}
          </span>
          <span className="flex h-6 flex-1 items-center rounded-md bg-surface-sunken">
            <span
              className="h-full rounded-md bg-brand transition-[width]"
              style={{ width: `${Math.max(row.value === 0 ? 0 : 4, (row.value / max) * 100)}%` }}
            />
          </span>
          <span className="w-6 shrink-0 text-right text-xs font-semibold tabular-nums">
            {row.value}
          </span>
        </li>
      ))}
    </ul>
  );
}
